import type { GeoJSONFeature, Hole } from '../api/types';
import { haversineMeters, type GeoPoint } from './geoPoint';
import type { GeoBoundingBox } from './geoProjection';
import { JavaRandom, treeSeedFor } from './javaRandom';

/**
 * Ported from wear/src/main/java/com/example/myrangefinder/ui/screens/round/geometry/HoleGeometry.kt.
 *
 * Parses a hole's GeoJSON into the shapes the viewer draws. Polygons are reduced to their
 * outer ring — interior holes are not used, same as the watch.
 */

export interface TeePoint {
  name: string | null;
  point: GeoPoint;
}

export interface HoleShapes {
  green: GeoPoint[];
  fairway: GeoPoint[];
  rough: GeoPoint[][];
  bunkers: GeoPoint[][];
  water: GeoPoint[][];
  outOfBounds: GeoPoint[][];
  tees: TeePoint[];
  /** Individual trees, both explicit Points and ones generated inside tree polygons. */
  trees: GeoPoint[];
  treeAreas: GeoPoint[][];
  pin: GeoPoint;
}

export interface GreenDistances {
  /** Nearest green vertex. */
  front: number;
  /** Distance to the arithmetic centroid of the vertices. */
  center: number;
  /** Farthest green vertex. */
  back: number;
}

export const EMPTY_GREEN_DISTANCES: GreenDistances = { front: 0, center: 0, back: 0 };

// ------------------------------------------------------------------ parse

function polygonOuterRing(coordinates: unknown): GeoPoint[] | null {
  if (!Array.isArray(coordinates)) return null;
  const outer = coordinates[0];
  if (!Array.isArray(outer)) return null;

  const ring: GeoPoint[] = [];
  for (const pair of outer) {
    if (!Array.isArray(pair) || pair.length < 2) continue;
    const [lng, lat] = pair as number[];
    if (typeof lng !== 'number' || typeof lat !== 'number') continue;
    ring.push({ longitude: lng, latitude: lat });
  }
  return ring.length >= 3 ? ring : null;
}

function pointCoords(coordinates: unknown): GeoPoint | null {
  if (!Array.isArray(coordinates) || coordinates.length < 2) return null;
  const [lng, lat] = coordinates as number[];
  if (typeof lng !== 'number' || typeof lat !== 'number') return null;
  return { longitude: lng, latitude: lat };
}

/**
 * Parses a hole into drawable shapes.
 *
 * Note what is *not* handled: `LineString` features are ignored. The backend's schema allows
 * them (a cart path, say) but the watch's parser drops them silently, and this port keeps
 * that behaviour so the two apps draw the same hole. Worth revisiting together, not here.
 */
export function parseHole(hole: Hole): HoleShapes {
  const rough: GeoPoint[][] = [];
  const bunkers: GeoPoint[][] = [];
  const water: GeoPoint[][] = [];
  const outOfBounds: GeoPoint[][] = [];
  const tees: TeePoint[] = [];
  const trees: GeoPoint[] = [];
  const treeAreas: GeoPoint[][] = [];
  let green: GeoPoint[] = [];
  let fairway: GeoPoint[] = [];

  const features: GeoJSONFeature[] = hole.features?.features ?? [];

  for (const feature of features) {
    const kind = feature.properties?.kind ?? '';

    if (feature.geometry.type === 'Polygon') {
      const ring = polygonOuterRing(feature.geometry.coordinates);
      if (!ring) continue;

      switch (kind) {
        case 'green':
          green = ring;
          break;
        case 'fairway':
          fairway = ring;
          break;
        case 'rough':
          rough.push(ring);
          break;
        case 'bunker':
          bunkers.push(ring);
          break;
        case 'water':
          water.push(ring);
          break;
        case 'ob':
          outOfBounds.push(ring);
          break;
        case 'tree':
          treeAreas.push(ring);
          // Generated here, in world coordinates, so the layout is immune to zoom and pan —
          // only the projection of each tree moves, never the tree itself.
          trees.push(...generateTreesInArea(ring));
          break;
        default:
          break;
      }
    } else if (feature.geometry.type === 'Point') {
      const point = pointCoords(feature.geometry.coordinates);
      if (!point) continue;
      if (kind === 'tee') {
        const teeName = typeof feature.properties.tee === 'string' ? feature.properties.tee : null;
        tees.push({ name: teeName, point });
      } else if (kind === 'tree') {
        trees.push(point);
      }
    }
  }

  return {
    green,
    fairway,
    rough,
    bunkers,
    water,
    outOfBounds,
    tees,
    trees,
    treeAreas,
    pin: { longitude: hole.pin.coordinates[0], latitude: hole.pin.coordinates[1] },
  };
}

// -------------------------------------------------------------- distances

export function centroid(points: GeoPoint[]): GeoPoint {
  if (points.length === 0) return { longitude: 0, latitude: 0 };
  let lng = 0;
  let lat = 0;
  for (const p of points) {
    lng += p.longitude;
    lat += p.latitude;
  }
  return { longitude: lng / points.length, latitude: lat / points.length };
}

/**
 * Front / centre / back distances to the green, in metres.
 *
 * These are **vertex-based**, not edge-based: front is the nearest green *vertex*, not the
 * nearest point on the green's outline. On a coarse polygon the true nearest edge point can
 * be a few metres closer. That is the watch's definition and both apps must agree, so it is
 * reproduced rather than improved.
 *
 * Truncated to whole metres, like the watch.
 */
export function greenDistances(from: GeoPoint, green: GeoPoint[]): GreenDistances {
  if (green.length === 0) return EMPTY_GREEN_DISTANCES;

  const distances = green.map((p) => haversineMeters(from, p));
  return {
    front: Math.trunc(Math.min(...distances)),
    center: Math.trunc(haversineMeters(from, centroid(green))),
    back: Math.trunc(Math.max(...distances)),
  };
}

// ------------------------------------------------------------ bounding box

/** The smallest box containing every drawn feature, plus an optional extra point. */
export function shapesBoundingBox(shapes: HoleShapes, extra?: GeoPoint): GeoBoundingBox {
  let minLongitude = Infinity;
  let maxLongitude = -Infinity;
  let minLatitude = Infinity;
  let maxLatitude = -Infinity;

  const visit = (p: GeoPoint) => {
    if (p.longitude < minLongitude) minLongitude = p.longitude;
    if (p.longitude > maxLongitude) maxLongitude = p.longitude;
    if (p.latitude < minLatitude) minLatitude = p.latitude;
    if (p.latitude > maxLatitude) maxLatitude = p.latitude;
  };

  shapes.green.forEach(visit);
  shapes.fairway.forEach(visit);
  shapes.rough.forEach((ring) => ring.forEach(visit));
  shapes.bunkers.forEach((ring) => ring.forEach(visit));
  shapes.water.forEach((ring) => ring.forEach(visit));
  shapes.outOfBounds.forEach((ring) => ring.forEach(visit));
  shapes.tees.forEach((t) => visit(t.point));
  shapes.trees.forEach(visit);
  shapes.treeAreas.forEach((ring) => ring.forEach(visit));
  visit(shapes.pin);
  if (extra) visit(extra);

  return { minLongitude, maxLongitude, minLatitude, maxLatitude };
}

// ----------------------------------------------------------------- trees

const TREE_SPACING_METERS = 7;
const METERS_PER_DEG_LAT = 111_320;
/** Safety cap for very large polygons, matching the watch. */
const MAX_TREES_PER_AREA = 250;

/**
 * Fills a tree polygon with individual trees on a jittered triangular grid.
 *
 * Spacing is in metres, so density looks right regardless of how large the polygon is, and
 * the jitter is drawn from a `java.util.Random` seeded by the polygon's own coordinates —
 * see javaRandom.ts for why that generator had to be ported rather than substituted.
 */
export function generateTreesInArea(polygon: GeoPoint[]): GeoPoint[] {
  if (polygon.length < 3) return [];

  let minLng = Infinity;
  let maxLng = -Infinity;
  let minLat = Infinity;
  let maxLat = -Infinity;
  for (const p of polygon) {
    if (p.longitude < minLng) minLng = p.longitude;
    if (p.longitude > maxLng) maxLng = p.longitude;
    if (p.latitude < minLat) minLat = p.latitude;
    if (p.latitude > maxLat) maxLat = p.latitude;
  }

  const centerLat = (minLat + maxLat) / 2;
  const mPerDegLng = METERS_PER_DEG_LAT * Math.cos((centerLat * Math.PI) / 180);
  const stepLat = TREE_SPACING_METERS / METERS_PER_DEG_LAT;
  const stepLng = TREE_SPACING_METERS / mPerDegLng;

  const rng = new JavaRandom(treeSeedFor(minLng, minLat));

  const result: GeoPoint[] = [];
  let lat = minLat + stepLat / 2;
  let row = 0;

  while (lat < maxLat && result.length < MAX_TREES_PER_AREA) {
    // Every other row is offset half a step — triangular packing, not a square lattice.
    const rowOffset = row % 2 === 0 ? 0 : stepLng / 2;
    let lng = minLng + stepLng / 2 + rowOffset;

    while (lng < maxLng && result.length < MAX_TREES_PER_AREA) {
      const jLng = (rng.nextDouble() - 0.5) * stepLng * 0.55;
      const jLat = (rng.nextDouble() - 0.5) * stepLat * 0.55;
      const p = { longitude: lng + jLng, latitude: lat + jLat };
      if (pointInGeoPolygon(p, polygon)) result.push(p);
      lng += stepLng;
    }

    lat += stepLat;
    row++;
  }

  return result;
}

/** Ray casting. Transcribed from the watch, including its 1e-12 guard on horizontal edges. */
export function pointInGeoPolygon(p: GeoPoint, polygon: GeoPoint[]): boolean {
  if (polygon.length < 3) return false;

  let inside = false;
  let j = polygon.length - 1;

  for (let i = 0; i < polygon.length; i++) {
    const pi = polygon[i];
    const pj = polygon[j];
    if (!pi || !pj) continue;

    const crossesY = pi.latitude > p.latitude !== pj.latitude > p.latitude;
    if (!crossesY) {
      j = i;
      continue;
    }

    const dyRaw = pj.latitude - pi.latitude;
    const dy = dyRaw !== 0 ? dyRaw : 1e-12;
    const xAtY = ((pj.longitude - pi.longitude) * (p.latitude - pi.latitude)) / dy + pi.longitude;
    if (p.longitude < xAtY) inside = !inside;

    j = i;
  }

  return inside;
}
